import api from "../lib/api";
import PageHeader, { PageBody, Card, useFetch } from "../components/Shared";
import { toast } from "sonner";
import { MapTrifold, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";

export default function Territories() {
  const { data, loading, reload } = useFetch(() => api.get("/territories").then((r) => r.data), []);

  const recompute = async () => {
    try {
      const { data: res } = await api.post("/territories/recompute");
      toast.success(`Recomputado · ${res.upserts} territorios`);
      reload();
    } catch (err) { toast.error("Error"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/export/territories.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `heroes_migrantes_territories_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exportado");
    } catch (err) { toast.error("Error al exportar"); }
  };

  const byCountry = (data || []).reduce((acc, t) => {
    (acc[t.country] = acc[t.country] || []).push(t);
    return acc;
  }, {});

  return (
    <>
      <PageHeader
        label="Inteligencia territorial"
        title="Territorios"
        description="Engagement, founders y embajadores por país y municipio."
      >
        <button onClick={exportCsv} data-testid="export-territories-csv" className="bg-carbon-200 text-carbon-900 px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-carbon-300">
          <DownloadSimple size={14} /> Exportar CSV
        </button>
        <button onClick={recompute} data-testid="recompute-territories" className="bg-forest text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-forest/90">
          <ArrowsClockwise size={14} /> Recomputar
        </button>
      </PageHeader>
      <PageBody>
        {loading && <div className="text-carbon-500">Cargando...</div>}
        {!loading && Object.keys(byCountry).length === 0 && (
          <Card className="p-10 text-center text-carbon-400">
            <MapTrifold size={32} className="mx-auto mb-3 text-carbon-300" />
            Sin territorios. Cargar el seed desde Configuración.
          </Card>
        )}
        <div className="space-y-6">
          {Object.entries(byCountry).map(([country, ts]) => {
            const totalUsers = ts.reduce((s, t) => s + (t.total_users || 0), 0);
            const totalMigrant = ts.reduce((s, t) => s + (t.migrant_side_count || 0), 0);
            const totalOrigin = ts.reduce((s, t) => s + (t.origin_side_count || 0), 0);
            const totalFounders = ts.reduce((s, t) => s + (t.founder_count || 0), 0);
            const totalAmb = ts.reduce((s, t) => s + (t.ambassador_count || 0), 0);
            return (
              <Card key={country} className="overflow-hidden" testid={`country-${country}`}>
                <div className="px-5 py-4 bg-surface-alt border-b border-carbon-200 flex items-center justify-between">
                  <div>
                    <div className="section-label">País</div>
                    <h3 className="font-heading text-xl">{country}</h3>
                  </div>
                  <div className="flex gap-5 text-xs text-carbon-700 font-mono">
                    <span><b>{totalUsers}</b> users</span>
                    <span className="text-terracotta"><b>{totalMigrant}</b> migrant</span>
                    <span className="text-forest"><b>{totalOrigin}</b> origin</span>
                    <span><b>{totalFounders}</b> founders</span>
                    <span><b>{totalAmb}</b> embajadores</span>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-carbon-200 text-left">
                      <th className="px-5 py-3 section-label">Municipio</th>
                      <th className="px-5 py-3 section-label">Users</th>
                      <th className="px-5 py-3 section-label">Migrant-side</th>
                      <th className="px-5 py-3 section-label">Origin-side</th>
                      <th className="px-5 py-3 section-label">Founders</th>
                      <th className="px-5 py-3 section-label">Embajadores</th>
                      <th className="px-5 py-3 section-label">Engagement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ts.map((t) => (
                      <tr key={t.id} className="border-b border-carbon-100 hover:bg-page">
                        <td className="px-5 py-3 font-medium">{t.municipality || "—"}</td>
                        <td className="px-5 py-3 font-mono">{t.total_users}</td>
                        <td className="px-5 py-3 font-mono text-terracotta">{t.migrant_side_count ?? 0}</td>
                        <td className="px-5 py-3 font-mono text-forest">{t.origin_side_count ?? 0}</td>
                        <td className="px-5 py-3 font-mono text-terracotta">{t.founder_count}</td>
                        <td className="px-5 py-3 font-mono text-forest">{t.ambassador_count}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-carbon-100 rounded">
                              <div className="h-full bg-terracotta rounded" style={{ width: `${Math.min(100, (t.engagement_score || 0) * 100)}%` }} />
                            </div>
                            <span className="font-mono text-xs">{(t.engagement_score || 0).toFixed(2)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            );
          })}
        </div>
      </PageBody>
    </>
  );
}
